import { put, select, takeEvery } from "redux-saga/effects";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { Lead } from "@mapmycustomers/shared/types/entity";

import {
  clearLeadFinderLocation,
  clearLeadFinderRecords,
  removeLeadFinderRecords,
  setCurrentBounds,
  setLeadAnnotation,
} from "@app/component/leadFinder/store/actions";
import { handleError } from "@app/store/errors/actions";

import {
  appendCreatedRecordsToDraft,
  clearLeadSelection,
  setLeadFinderResultsVisible,
  updateRouteStops,
} from "../actions";
import { getRouteEditorDisplayStops, getSelectedLeadPlaceIds } from "../selectors";
import { EditorRouteStop } from "../types/RouteStop";

export function* onAppendCreatedRecordsToDraft({
  payload,
}: ReturnType<typeof appendCreatedRecordsToDraft>) {
  try {
    const draftStops: ReadonlyArray<EditorRouteStop> = yield select(getRouteEditorDisplayStops);
    const existingEntityIds = new Set(
      draftStops.flatMap((stop) => {
        if (stop.entity === EntityType.COMPANY && payload.entityType === EntityType.COMPANY) {
          return [stop.accountId];
        }
        if (stop.entity === EntityType.PERSON && payload.entityType === EntityType.PERSON) {
          return [stop.contactId];
        }
        return [];
      })
    );
    const addEntityIds = payload.entityIds.filter((entityId) => !existingEntityIds.has(entityId));
    if (addEntityIds.length) {
      yield put(updateRouteStops.request({ addEntityIds, entityType: payload.entityType }));
    }
    // Once records are created, drop the added leads from the shared results so they render
    // as records (route stops) instead of selectable leads and can't be added again.
    if (payload.entityIds.length) {
      const addedLeadPlaceIds: Array<Lead["place_id"]> = yield select(getSelectedLeadPlaceIds);
      if (addedLeadPlaceIds.length) {
        yield put(removeLeadFinderRecords({ placeIds: addedLeadPlaceIds }));
      }
    }
    yield put(clearLeadSelection());
  } catch (error) {
    yield put(handleError({ error }));
  }
}

// Symmetric with the map's exit-reset (onExitLeadFinderMode): tearing down the route
// results panel must clear the SHARED lead-finder slice so a later Map Lead Finder
// session doesn't render this route's stale results/bounds.
export function* onSetLeadFinderResultsVisible({
  payload,
}: ReturnType<typeof setLeadFinderResultsVisible>) {
  try {
    if (!payload) {
      yield put(clearLeadFinderRecords());
      yield put(clearLeadFinderLocation());
      yield put(setCurrentBounds({ bounds: undefined }));
      yield put(setLeadAnnotation(undefined));
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* routeLeadFinderSagas() {
  yield takeEvery(appendCreatedRecordsToDraft, onAppendCreatedRecordsToDraft);
  yield takeEvery(setLeadFinderResultsVisible, onSetLeadFinderResultsVisible);
}
